import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseApiService, PagedRequest, PagedResponse } from '../base/base-api.service';
import type { Customer } from './customer.service';
import type { Vessel } from './vessel.service';
import type { InsuranceDto } from './compliance.service';

// These types should match the models from @marina/models
export interface TerminationRequestEntryDto {
  id: string;
  contractName: string;
  customerName: string;
  vesselName: string;
  terminationDate: string | null;
  reason: string | null;
  contractOwner: string | null;
}

export interface RenewalEntryDto {
  id: string;
  contractName: string;
  customerName: string;
  vesselName: string;
  renewalDate: string | null;
  daysRemaining: number;
  contractOwner: string | null;
}

/** Per-status counts returned by GET /api/v1/portal/contracts/status-counts */
export interface ContractStatusCounts {
  all: number;
  draft: number;
  awaitingCustomerSignature: number;
  awaitingMarinaSignature: number;
  awaitingInitialPayment: number;
  contracted: number;
  pendingTermination: number;
  terminated: number;
  expired: number;
}

export interface Lease {
  id: string;
  customerId: string;
  /** Embedded in list responses — null for single get-by-id. */
  customerName?: string | null;
  slipId: string;
  dryDockLocationId?: string | null; // For dry storage
  vesselId: string;
  /** Embedded in list responses — null for single get-by-id. */
  vesselName?: string | null;
  startDate: string;
  endDate: string;
  monthlyRate: number;
  taxRate?: number;
  depositAmount: number;
  dueDay?: number;
  lateFeePercent?: number;
  gracePeriodDays?: number;
  renewalNoticeDays?: number;
  terminationNoticeDays?: number;
  /** Contract status enum ID (0=Draft, ..., 7=Expired) */
  status: number;
  /** MONTH_TO_MONTH | FIXED_TERM */
  termType?: string;
  contractPdfUrl?: string;
  docusignEnvelopeId?: string; // One envelope per contract
  signatureName?: string;
  signedDate?: string;
  marinaSignatureName?: string;
  marinaSignedDate?: string;
  createdAt: string;
  updatedAt: string;
  /** Slip or dry dock location name (e.g. "A-12") */
  slipName?: string | null;
  /** True when contract was signed on paper and entered by staff. */
  isPaperSigned?: boolean;

  /** Stripe subscription id (if subscription has been created). */
  stripeSubscriptionId?: string | null;
  stripeSubscriptionStatus?: string | null;
  subscriptionCreatedAt?: string | null;
  subscriptionCurrentPeriodEnd?: string | null;

  /** Activation workflow state/error (for staff troubleshooting). */
  activationStep?: string | null;
  activationError?: string | null;
}

export interface CreateLeaseRequest {
  customerId: string;
  slipId?: string; // For wet slips
  dryDockLocationId?: string; // For dry storage
  vesselId: string;
  startDate: string;
  endDate?: string;
  monthlyRate: number;
  termType?: string;
  taxRate?: number;
  depositAmount?: number;
  paymentFrequency?: string;
  dueDay?: number;
  lateFeePercent?: number;
  gracePeriodDays?: number;
  renewalNoticeDays?: number;
  terminationNoticeDays?: number;
  autoRenew?: boolean;
  insuranceVerified?: boolean;
  stormAddendumSigned?: boolean;
  /** True when contract is already signed on paper and entered by staff. */
  isPaperSigned?: boolean;
  createdBy?: string;
}

export interface LeaseFilterRequest extends PagedRequest {
  /** "ALL" or enum ID as string ("0"-"7") or enum name (e.g. "Contracted") */
  status?: string;
  termType?: string; // ALL, MONTH_TO_MONTH, FIXED_TERM
}

export interface UpdateLeaseRequest {
  customerId?: string;
  vesselId?: string;
  slipId?: string;
  termType?: string;
  startDate?: string;
  endDate?: string;
  monthlyRate?: number;
  taxRate?: number;
  depositAmount?: number;
  paymentFrequency?: string;
  dueDay?: number;
  lateFeePercent?: number;
  gracePeriodDays?: number;
  renewalNoticeDays?: number;
  terminationNoticeDays?: number;
  autoRenew?: boolean;
  insuranceVerified?: boolean;
  stormAddendumSigned?: boolean;
  contractPdfUrl?: string;
  status?: number;
}

export interface TerminateLeaseRequest {
  terminationDate: string;
  reason: string;
}

/** Shape returned by GET /api/v1/portal/contracts/{id}/details */
export interface ContractDetails {
  contract: Lease;
  customer: Customer | null;
  vessel: Vessel | null;
  storageLocation: {
    id: string;
    name: string;
    description?: string | null;
    rackId?: string | null;
    level?: number | null;
    positionIndex?: number | null;
  } | null;
  insurance: InsuranceDto | null;
  /** True when a signed PDF exists in storage (internal signing or DocuSign). */
  hasSignedContractPdf?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LeaseService extends BaseApiService {
  constructor(http: HttpClient) {
    super(http);
  }
  /**
   * Get contracts with renewal dates within the specified window (30 or 60 days).
   * Includes overdue and upcoming renewals. Supports limit/offset for dashboard widgets.
   */
  getRenewals(days: 30 | 60 = 30, limit = 10, offset = 0): Observable<PagedResponse<RenewalEntryDto>> {
    let params = new HttpParams().set('days', days.toString());
    if (limit > 0) params = params.set('limit', limit.toString());
    if (offset > 0) params = params.set('offset', offset.toString());
    return this.get<PagedResponse<RenewalEntryDto>>(`leases/renewals`, params);
  }

  /**
   * Get outstanding contract termination requests (PendingTermination status).
   * Supports limit/offset for dashboard widgets.
   */
  getPendingTerminations(limit = 10, offset = 0): Observable<PagedResponse<TerminationRequestEntryDto>> {
    let params = new HttpParams();
    if (limit > 0) params = params.set('limit', limit.toString());
    if (offset > 0) params = params.set('offset', offset.toString());
    return this.get<PagedResponse<TerminationRequestEntryDto>>(`leases/termination-requests`, params);
  }

  /**
   * Get lease by ID
   */
  getById(id: string): Observable<Lease> {
    return this.get<Lease>(`leases/${id}`);
  }

  /** Staff action: create Stripe subscription for an existing contract. */
  activateSubscription(contractId: string): Observable<Lease> {
    return this.post<Lease>(`leases/${contractId}/subscription/activate`, {});
  }

  /**
   * Contracts that nothing is currently billing.
   *
   * Subscription creation during onboarding is intentionally non-fatal, so a transient Stripe
   * error cannot block a dock master mid-contract. Rent is billed only by Stripe, so anything
   * listed here earns nothing until someone acts on it.
   */
  getLeasesNeedingBillingAttention(): Observable<LeaseBillingAttention[]> {
    return this.get<LeaseBillingAttention[]>('leases/billing-attention');
  }

  /**
   * Returns per-status contract counts in one request (GROUP BY on the backend).
   * Use this to populate tab badges instead of calling getAllFiltered N times with pageSize=1.
   */
  getContractStatusCounts(): Observable<ContractStatusCounts> {
    return this.get<ContractStatusCounts>('v1/portal/contracts/status-counts');
  }

  /**
   * Aggregated details endpoint — returns contract + customer + vessel + location + insurance
   * in a single request, shaped for the Storage Contract Details screen.
   */
  getContractDetails(contractId: string): Observable<ContractDetails> {
    return this.get<ContractDetails>(`v1/portal/contracts/${contractId}/details`);
  }

  /**
   * Get all leases with pagination
   */
  getAll(request?: PagedRequest): Observable<PagedResponse<Lease>> {
    return this.getPaged<Lease>('leases', request);
  }

  /**
   * Get all leases with filtering and sorting
   */
  getAllFiltered(request?: LeaseFilterRequest): Observable<PagedResponse<Lease>> {
    // Build params including status and termType
    let params = this.getPagedParams(request);
    
    if (request?.status) {
      params = params.set('status', request.status);
    }
    if (request?.termType) {
      params = params.set('termType', request.termType);
    }
    
    return this.get<PagedResponse<Lease>>('leases/filtered', params);
  }

  /**
   * Get leases by customer ID
   */
  getByCustomerId(customerId: string, request?: PagedRequest): Observable<PagedResponse<Lease>> {
    return this.getPaged<Lease>(`leases/customer/${customerId}`, request);
  }

  /**
   * Get leases by slip ID
   */
  getBySlipId(slipId: string, request?: PagedRequest): Observable<PagedResponse<Lease>> {
    return this.getPaged<Lease>(`leases/slip/${slipId}`, request);
  }

  /** Leases occupying dry-storage slots (rack map / slip selection modal). */
  getByDryDockLocationIds(locationIds: string[]): Observable<Lease[]> {
    if (!locationIds?.length) {
      return of([]);
    }
    let params = new HttpParams();
    for (const id of locationIds) {
      params = params.append('locationIds', id);
    }
    return this.get<Lease[]>('leases/dry-dock-locations', params);
  }

  /**
   * Get leases by vessel ID
   */
  getByVesselId(vesselId: string, request?: PagedRequest): Observable<PagedResponse<Lease>> {
    // Note: Backend endpoint doesn't exist yet, so we filter getAll results
    // TODO: Add backend endpoint leases/vessel/{vesselId} for better performance
    return this.getAll(request).pipe(
      map(response => ({
        ...response,
        items: (response.items || []).filter(lease => lease.vesselId === vesselId)
      }))
    );
  }

  /**
   * Create new lease
   */
  create(request: CreateLeaseRequest): Observable<Lease> {
    return this.post<Lease>('leases', request);
  }

  /**
   * Update lease
   */
  update(id: string, request: UpdateLeaseRequest): Observable<Lease> {
    return this.put<Lease>(`leases/${id}`, request);
  }

  /**
   * Renew lease
   */
  renew(id: string, newEndDate: string): Observable<Lease> {
    return this.post<Lease>(`leases/${id}/renew`, { newEndDate });
  }

  /**
   * Activate lease
   */
  activate(id: string): Observable<Lease> {
    return this.post<Lease>(`leases/${id}/activate`, {});
  }

  /**
   * Expire lease
   */
  expire(id: string): Observable<Lease> {
    return this.post<Lease>(`leases/${id}/expire`, {});
  }

  /**
   * Update lease status (Generic transition)
   * @param depositDisposition When terminating with deposit: 'Refund' (default) or 'ApplyToFinalInvoice'
   */
  updateStatus(id: string, status: number, reason: string, refundAmount?: number, refundMethod?: string, depositDisposition?: string): Observable<Lease> {
    const body: Record<string, unknown> = { status, reason };
    if (refundAmount != null) body['refundAmount'] = refundAmount;
    if (refundMethod) body['refundMethod'] = refundMethod;
    if (depositDisposition) body['depositDisposition'] = depositDisposition;
    return this.patch<Lease>(`leases/${id}/status`, body);
  }

  /**
   * Resend contract email
   */
  resendEmail(id: string): Observable<void> {
    return this.post<void>(`leases/${id}/email/resend`, {});
  }

  /**
   * Get PDF download URL
   */
  getPdfDownloadUrl(id: string): string {
    return `${this.baseUrl}/leases/${id}/pdf`;
  }

  /**
   * Download PDF as Blob
   */
  downloadPdf(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/leases/${id}/pdf`, { responseType: 'blob' });
  }

  /**
   * Terminate lease
   */
  terminate(id: string, terminationDate: string, reason?: string): Observable<Lease> {
    return this.post<Lease>(`leases/${id}/terminate`, { terminationDate, reason });
  }

  /**
   * Delete lease
   */
  deleteLease(id: string): Observable<void> {
    return this.deleteRequest(`leases/${id}`);
  }

  /**
   * Get insurance status for a vessel
   * Returns: 'valid' | 'expired' | 'not_verified' | 'missing'
   */
  getInsuranceStatus(vesselId: string): Observable<{ status: string }> {
    return this.get<{ status: string }>(`leases/vessel/${vesselId}/insurance-status`);
  }

  /**
   * Process activation step for a contract
   */
  processActivation(leaseId: string): Observable<{ success: boolean; currentStep: string; nextStep: string; errorMessage?: string; isComplete: boolean }> {
    return this.post<{ success: boolean; currentStep: string; nextStep: string; errorMessage?: string; isComplete: boolean }>(`leases/${leaseId}/activation/process`, {});
  }

  /**
   * Get activation status for a contract
   */
  getActivationStatus(leaseId: string): Observable<{ currentStep: string; contractStatus: string; requiresDeposit: boolean; depositPaid: boolean; subscriptionCreated: boolean; firstPaymentPaid: boolean; errorMessage?: string; canRetry: boolean }> {
    return this.get<{ currentStep: string; contractStatus: string; requiresDeposit: boolean; depositPaid: boolean; subscriptionCreated: boolean; firstPaymentPaid: boolean; errorMessage?: string; canRetry: boolean }>(`leases/${leaseId}/activation/status`);
  }

  /**
   * Retry failed activation step
   */
  retryActivation(leaseId: string): Observable<{ success: boolean; currentStep: string; nextStep: string; errorMessage?: string; isComplete: boolean }> {
    return this.post<{ success: boolean; currentStep: string; nextStep: string; errorMessage?: string; isComplete: boolean }>(`leases/${leaseId}/activation/retry`, {});
  }

  /**
   * Create deposit invoice if needed and process payment
   */
  payDeposit(leaseId: string): Observable<{ success: boolean; currentStep: string; nextStep: string; errorMessage?: string; isComplete: boolean }> {
    return this.post<{ success: boolean; currentStep: string; nextStep: string; errorMessage?: string; isComplete: boolean }>(`leases/${leaseId}/deposit/pay`, {});
  }
}

export type LeaseBillingAttentionReason =
  | 'SubscriptionCreationFailed'
  | 'ContractedWithoutSubscription'
  | 'AwaitingOnboarding';

export interface LeaseBillingAttention {
  leaseId: string;
  customerId: string;
  customerName: string;
  vesselName?: string | null;
  monthlyRate: number;
  startDate: string;
  status: string;
  activationStep?: string | null;
  /** Why Stripe never started billing, when known. */
  activationError?: string | null;
  reason: LeaseBillingAttentionReason;
  /** Phrased for the person who has to act on it. */
  recommendedAction: string;
  /** False when the customer has no saved card, so the fix needs them first. */
  canActivateSubscription: boolean;
  daysUnbilled: number;
}
